import {
  ArrowRight,
  Construction,
  Sparkles,
  Star,
  Zap,
} from 'lucide-react';
import { useTheme } from 'components/core/theme-provider';
import { useTranslation } from 'react-i18next';

export default function ComingSoonPage() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const headline = t('COMING_SOON_HEADLINE');
  const progress = Math.floor(Math.random() * 21) + 75;
  const previewCardBackground = theme === 'dark' ? '#5f6f86' : '#3078a9';
  const featurePillars = [
    {
      icon: Sparkles,
      title: 'Thoughtful experience',
      description: 'Designed to feel smooth, modern, and genuinely useful.',
    },
    {
      icon: Zap,
      title: 'Faster workflows',
      description: 'Built to help teams move with more clarity and less friction.',
    },
    {
      icon: Star,
      title: 'Worth the wait',
      description: 'We are polishing the details before opening the doors.',
    },
  ];

  return (
    <div className="mx-auto my-6 w-full bg-background px-6 py-8 sm:px-8 lg:px-10">
      <div className="relative grid min-h-[520px] items-center gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-10">
        <div className="space-y-6">
          <div className="inline-flex dark:text-true-white items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-primary">
            <Sparkles className="h-4 w-4" />
            {t('COMING_SOON') || 'Coming Soon'}
          </div>

          <div className="space-y-4">
            <h1 className="max-w-3xl text-4xl font-semibold leading-tight tracking-[-0.04em] text-foreground sm:text-5xl lg:text-6xl">
              {headline === 'COMING_SOON_HEADLINE'
                ? 'We are building great things for you'
                : headline}
            </h1>
            <p className="max-w-2xl text-base leading-7 text-medium-emphasis sm:text-lg">
              {t('COMING_SOON_DESCRIPTION') ||
                'A more refined experience is on the way. We are shaping something bold, useful, and beautifully crafted behind the scenes.'}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            {featurePillars.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="rounded-3xl border border-border bg-card p-4 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5 dark:text-true-white" />
                </div>
                <h2 className="mb-2 text-sm font-semibold text-foreground">{title}</h2>
                <p className="text-sm leading-6 text-medium-emphasis">{description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="relative overflow-hidden rounded-[32px] border border-border bg-card p-6 shadow-sm sm:p-7">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg">
                  <Construction className="h-7 w-7" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary/80">
                    In Progress
                  </p>
                  <p className="text-lg font-semibold text-foreground">Launch sequence</p>
                </div>
              </div>
              <div className="rounded-full border border-primary/15 bg-primary/10 px-3 py-1 text-xs font-medium text-primary dark:text-true-white">
                2026
              </div>
            </div>

            <div className="space-y-4">
              <div
                className="rounded-3xl p-5 text-white"
                style={{ backgroundColor: previewCardBackground }}
              >
                <div className="mb-8 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-white/70">
                      Preview
                    </p>
                    <p className="mt-2 text-2xl font-semibold leading-tight">
                      Something special is taking shape.
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white/10 p-3">
                    <ArrowRight className="h-5 w-5" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm text-white/75">
                    <span>Crafting the experience</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-white/15">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-3xl border border-border bg-background p-5">
                  <p className="text-xs uppercase tracking-[0.28em] text-medium-emphasis">
                    Focus
                  </p>
                  <p className="mt-3 text-lg font-semibold text-foreground">
                    Elegant design with practical value
                  </p>
                </div>
                <div className="rounded-3xl border border-border bg-background p-5">
                  <p className="text-xs uppercase tracking-[0.28em] text-medium-emphasis">
                    Promise
                  </p>
                  <p className="mt-3 text-lg font-semibold text-foreground">
                    Built carefully, not rushed out
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
